import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PerformanceResultUserFragment } from '../../PerformanceResult.api';
import { type AssessmentRunOption } from '../../shared/utils';
import March20kmInnerForm from '../March20KmInnerForm';
import {
    createMarch20KmPerformanceFormSchema,
    march20KmPerformanceDefaultFormValues,
    type CreateMarch20KmPerformanceFormData,
} from '../March20KmPerformanceFormConfig';
import {
    SubmitPerformanceMarch20KmResultDocument,
    type SubmitPerformanceMarch20KmResultMutation,
} from './CreatePerformanceMarch20KmResults.api';
import client from '@/apolloClient';
import { UserRole, type SubmitMarch20KmResultInput } from '@/apolloClient.types';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useGetCompanyId, useUser } from '@/contexts/UserSession';
import { mapPassFailWithNullToResult } from '@/utils/common';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';

interface CreatePerformanceMarch20KmModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    groupUsers: PerformanceResultUserFragment[];
    assessmentRunOptions: AssessmentRunOption[];
}

const CreatePerformanceMarch20KmModal = ({
    open,
    onClose,
    groupUsers,
    assessmentRunOptions,
}: CreatePerformanceMarch20KmModalProps) => {
    const { t } = useTranslation('performanceResult');
    const selectedCompanyId = useGetCompanyId();
    const { getRoleLabel } = useCompanyRoleLabels();
    const user = useUser(true);

    const isTrainee = useMemo(() => {
        return user.__typename === 'TenantUser' && user.role === UserRole.Trainee;
    }, [user]);

    const form = useForm<CreateMarch20KmPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(createMarch20KmPerformanceFormSchema),
        defaultValues: { ...march20KmPerformanceDefaultFormValues, userId: isTrainee ? user.id : undefined },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    useEffect(() => {
        if (!isTiedToAssessmentRun) {
            form.setValue('assessmentRunId', null);
        }
    }, [form, isTiedToAssessmentRun]);

    const handleSubmit = useCallback(async () => {
        if (!(await form.trigger())) {
            return;
        }

        const values = form.getValues();

        const request: SubmitMarch20KmResultInput = {
            assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
            userId: values.userId,
            companyId: selectedCompanyId || '',
            completedAt: values.date,
            result: {
                timingSec: values.timingSec ?? undefined,
                result: mapPassFailWithNullToResult(values.result),
            },
        };

        try {
            const { data } = await client.mutate<SubmitPerformanceMarch20KmResultMutation>({
                mutation: SubmitPerformanceMarch20KmResultDocument,
                variables: {
                    input: request,
                },
            });

            if (data?.submitMarch20KmResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.submitMarch20KmResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage =
                    specificErrorField?.message || t('createError.description', { type: '20km Combat March' });

                showErrorNotification({
                    message: t('createError.title', { type: '20km Combat March' }),
                    description: errorMessage,
                });
                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('createSuccess.title', { type: '20km Combat March' }),
                    description: t('createSuccess.message', { type: '20km Combat March' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error submitting 20km Combat March performance result:', error);
            throw error;
        }
    }, [form, onClose, selectedCompanyId, t]);

    const userOptions = useMemo(() => {
        const options = [];
        for (const user of groupUsers) {
            if (user.__typename === 'TenantUser') {
                options.push({ label: user.name, value: user.id });
            }
        }
        return options;
    }, [groupUsers]);

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button outline onClick={() => onClose()}>
                    {t('march20km.createModal.cancelBtn')}
                </Button>
                <Button color="teal" onClick={handleSubmit}>
                    {t('march20km.createModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('march20km.createModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <SelectField
                                name="userId"
                                label={t('march20km.form.traineeName', { role: getRoleLabel(UserRole.Trainee) })}
                                placeholder={t('march20km.form.traineeNamePlaceholder', {
                                    role: getRoleLabel(UserRole.Trainee).toLocaleLowerCase(),
                                })}
                                searchable
                                options={userOptions}
                                required
                                disabled={isTrainee}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('march20km.form.assessmentDate')} required />
                        </div>
                        <March20kmInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default CreatePerformanceMarch20KmModal;
